// Actuals → Periods (PRD AC7, AC9; P2c): close a month once its last week is locked, reopen it with a
// reason to correct it, and the Monday reminder: preview what anyone's says and send it now.

import { useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Input, Select, Subtitle2 } from "@fluentui/react-components";
import { errorText, send, useApi } from "../api";
import { stamp } from "../format";
import { ErrorBar, Loading, Warnings } from "../ui";

interface PeriodsResponse {
  cutover: string;
  etag: string | null;
  months: { month: string; closed: boolean; lastWeekOpen: boolean; toReview: number; inProgress: boolean }[];
  history: { month: string; action: "close" | "reopen"; by: string; at: string; reason?: string }[];
}

export function Periods({ names }: { names: Map<string, string> }) {
  const data = useApi<PeriodsResponse>("/periods");
  const [reopening, setReopening] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const d = data.data;

  async function change(month: string, action: "close" | "reopen") {
    setBusy(month);
    setError(undefined);
    try {
      await send("POST", `/periods/${action}`, { month, etag: d?.etag ?? null, ...(action === "reopen" ? { reason } : {}) });
      setReopening(null);
      setReason("");
      data.reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(null);
  }

  return (
    <>
      <ErrorBar message={data.error ?? error} />
      <Card className="panel wide">
        <Subtitle2>Months</Subtitle2>
        <Caption1 className="block">
          Closing a month freezes its hours: the calendar sync, imports, time cards and the inbox leave its days alone. Close a month after its last week
          locks (Tuesday 12:00 after it ends), once its inbox is worked. Reopen it, with a reason, to correct it.
        </Caption1>
        {!d ? (
          <Loading />
        ) : (
          <table className="edit-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Status</th>
                <th className="num">To review</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.months.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td>
                    {m.closed ? (
                      <Badge appearance="tint" color="success">
                        Closed
                      </Badge>
                    ) : m.inProgress ? (
                      <Caption1 className="muted">In progress</Caption1>
                    ) : m.lastWeekOpen ? (
                      <Caption1 className="muted">Last week still open</Caption1>
                    ) : (
                      <Caption1>Ready to close</Caption1>
                    )}
                  </td>
                  <td className="num">{m.toReview || ""}</td>
                  <td>
                    {m.closed ? (
                      reopening === m.month ? (
                        <div className="toolbar">
                          <Input size="small" placeholder="Why reopen?" value={reason} onChange={(_, x) => setReason(x.value)} aria-label="Reason for reopening" />
                          <Button size="small" disabled={!reason.trim() || busy === m.month} onClick={() => void change(m.month, "reopen")}>
                            Reopen
                          </Button>
                          <Button size="small" appearance="subtle" onClick={() => setReopening(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button size="small" appearance="subtle" onClick={() => setReopening(m.month)}>
                          Reopen…
                        </Button>
                      )
                    ) : (
                      !m.inProgress &&
                      !m.lastWeekOpen && (
                        <Button size="small" appearance="primary" disabled={busy === m.month} onClick={() => void change(m.month, "close")} title={m.toReview ? `${m.toReview} inbox items are still open in this month` : undefined}>
                          Close {m.month}
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {d && d.history.length > 0 && (
          <>
            <Subtitle2>History</Subtitle2>
            {d.history.map((x, i) => (
              <Caption1 key={i} className="block">
                {stamp(x.at)} · {x.by} {x.action === "close" ? "closed" : "reopened"} {x.month}
                {x.reason ? `: ${x.reason}` : ""}
              </Caption1>
            ))}
          </>
        )}
      </Card>
      <ReminderCard names={names} />
    </>
  );
}

/** Preview anyone's Monday reminder for last week, and send it now. */
function ReminderCard({ names }: { names: Map<string, string> }) {
  const people = [...names.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const [personId, setPersonId] = useState("");
  const who = personId || people[0]?.[0] || "";
  const preview = useApi<{ monday: string; to: string | null; reminder: { gaps: number; fix: number; text: string } | null }>(who ? `/reminders/preview?personId=${encodeURIComponent(who)}` : null);
  const [done, setDone] = useState<string>();
  const [error, setError] = useState<string>();
  async function sendNow(all: boolean) {
    setError(undefined);
    setDone(undefined);
    try {
      const r = await send<{ sent: number; skipped: number }>("POST", "/reminders/send", all ? {} : { personId: who });
      setDone(`${r.sent} sent${r.skipped ? `, ${r.skipped} already sent for that week` : ""}.`);
    } catch (err) {
      setError(errorText(err));
    }
  }
  const p = preview.data;
  return (
    <Card className="panel wide">
      <Subtitle2>Monday reminder</Subtitle2>
      <Caption1 className="block">
        At 08:00 Central each Monday, everyone with something to fix in the week just ended gets one email from assistant@ listing only their own gaps and
        uncategorized events. Turn it on in Settings → Planning defaults. Here you can see what anyone's says, and send it now.
      </Caption1>
      <div className="toolbar">
        <Select size="small" value={who} onChange={(_, x) => setPersonId(x.value)} aria-label="Person">
          {people.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
        <Button size="small" disabled={!p?.reminder} onClick={() => void sendNow(false)}>
          Send theirs now
        </Button>
        <Button size="small" appearance="subtle" onClick={() => void sendNow(true)}>
          Send everyone's now
        </Button>
      </div>
      <ErrorBar message={preview.error ?? error} />
      {done && <Warnings intent="info" items={[done]} />}
      {preview.loading && !p ? (
        <Loading />
      ) : p?.reminder ? (
        <pre className="email-preview">{`To: ${p.to}\n\n${p.reminder.text}`}</pre>
      ) : p ? (
        <Body1 className="muted">Nothing to fix for the week of {p.monday}, so no email.</Body1>
      ) : null}
    </Card>
  );
}
